import { RangeValue } from "@evergis/ui";
import { StyleDc } from "@evergis/api";

import { map } from "../map";
import { StyleProxy } from "./classification/StyleProxy";

const { levels } = map.tileScheme;

type StyleProxyPart = Pick<StyleProxy, "scale">;

export const getSafetyScale = (style: StyleProxyPart, defaultScaleRange: RangeValue): RangeValue =>
  (style && style.scale) || defaultScaleRange;

export const scaleIs = (style: StyleProxyPart, equalValue: RangeValue, defaultScaleRange: RangeValue): boolean => {
  const [min, max] = getSafetyScale(style, defaultScaleRange);

  return equalValue[0] === min && equalValue[1] === max;
};

export const scaleIsDefault = (style: StyleProxyPart, defaultScaleRange: RangeValue): boolean => {
  return scaleIs(style, defaultScaleRange, defaultScaleRange);
};

export const scaleIsVisible = (style: StyleProxyPart, mapLevel: number, defaultScaleRange: RangeValue): boolean => {
  if (!style) {
    return true;
  }

  const [min, max] = getSafetyScale(style, defaultScaleRange);

  return scaleIsDefault(style, defaultScaleRange) || (mapLevel >= min && mapLevel <= max);
};

export const getMinMax = (nums: number[]): RangeValue => {
  if (!nums) return [null, null];

  return [Math.min(...nums), Math.max(...nums)];
};

export const castScaleToResolution = (scale: Nullable<RangeValue>): RangeValue => {
  const [min, max] = getMinMax(scale);

  const { minRes, maxRes } = map.tileScheme.levels.reduce(
    ({ minRes: currMinRes, maxRes: currMaxRes }, level) => ({
      minRes: currMinRes || (level.zIndex === max ? level.resolution : 0),
      maxRes: currMaxRes || (level.zIndex === min ? level.resolution : 0),
    }),
    {
      minRes: null,
      maxRes: null,
    },
  );

  return [minRes, maxRes];
};

export const castResolutionToScale = (
  resolution: Pick<StyleDc, "minResolution" | "maxResolution">,
  defaultScaleRange: RangeValue,
): RangeValue => {
  if (!resolution) return null;

  const { minResolution, maxResolution } = resolution;
  const { minScale, maxScale } = levels.reduce(
    ({ minScale: currMinScale, maxScale: currMaxScale }, level) => ({
      minScale: (level.resolution === maxResolution && level.zIndex) || currMinScale,
      maxScale: (level.resolution === minResolution && level.zIndex) || currMaxScale,
    }),
    {
      minScale: defaultScaleRange?.[0],
      maxScale: defaultScaleRange?.[1],
    },
  );

  return getMinMax([minScale, maxScale]);
};

export const prettifyScale = ([min, max]: RangeValue): string => (min === max ? `${min}` : `${min}-${max}`);
